// ModalShell — 모든 모달의 표준 골격.
//
// 기존 Modal(딤 배경 + 닫기 X 껍데기) 위에 "헤더 / 자동 크기 본문 / 푸터" 3단 레이아웃을
// 한 곳에서 강제하는 중간층이다. 지금까지 40여 개 모달이 각자 손으로 짜던
//   <Modal className="max-w-lg flex flex-col max-h-[85vh]">
//     <div className="px-5 py-3 border-b ...">제목 + X</div>     ← 헤더
//     <div className="flex-1 overflow-y-auto px-5 py-4">...</div> ← 본문(스크롤)
//     <div className="px-5 py-3 border-t flex justify-end">...</div> ← 푸터
// 보일러플레이트를 흡수한다.
//
// 핵심 동작(요청 "데이터 양에 따라 실시간으로 크기 변함"):
//   panel = flex flex-col + max-h(기본 85vh), 본문 = min-h-0 flex-1 overflow-y-auto.
//   → 내용이 적으면 그만큼만 차지하고, 화면을 넘으면 헤더·푸터는 고정한 채 본문만 스크롤한다.
//
// 함정 해소: 기존 Modal 은 children 을 클래스 없는 <div> 로 한 겹 감싸서 flex/height 전파가
//   끊긴다(그래서 TemplatePicker 가 내부 h-[88vh] 로 우회했다). ModalShell 은 base Modal 을
//   쓰지 않고 직접 portal + 오버레이를 구성해 panel 이 곧 flex 컨테이너가 되게 한다.
//   시각(딤 배경·블러·둥근 모서리·Esc 닫힘·body 스크롤 잠금)은 base Modal 과 동일하게 맞췄다.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useModalSize } from "../../../hooks/useModalSize";
import { useModalEsc } from "./modalStack";

export type ModalSize = "xs" | "sm" | "md" | "lg" | "xl" | "full" | "fill";

/** 실제 등장한 17종 폭을 6개로 수렴시킨 프리셋. 기본은 md(가장 두꺼운 558~720px 대역). */
const SIZE_CLASS: Record<ModalSize, string> = {
  xs: "max-w-xs", // ~20rem  단일 입력/식
  sm: "max-w-md", // 28rem   짧은 폼·확인
  md: "max-w-xl", // ~36rem  기본값
  lg: "max-w-3xl", // 48rem  2열 상세·중형 picker
  xl: "max-w-5xl", // 64rem  2-pane·넓은 상세
  full: "max-w-[min(96vw,1600px)]", // 풀페이지급
  fill: "max-w-none", // 쓸 수 있는 폭을 그대로 — 왼쪽 메뉴를 뺀 오른쪽 공간을 가로로 꽉 채운다
};

export interface ModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  /** 헤더 제목. 없으면 헤더 영역 자체를 그리지 않는다(headerExtra·subtitle 도 없을 때). */
  title?: ReactNode;
  /** 제목 아래 회색 보조 설명. */
  subtitle?: ReactNode;
  /** 폭 프리셋. 기본 md. */
  size?: ModalSize;
  /** auto=내용 따라 늘어남(기본), fixed=데이터 무관 고정 높이(maxHeight 만큼). */
  height?: "auto" | "fixed";
  /** panel 최대 높이. 기본 85vh. */
  maxHeight?: string;
  /** 헤더 우측 슬롯(편집 버튼·인라인 select·커스텀 액션바). 닫기 X 왼쪽에 놓인다. */
  headerExtra?: ReactNode;
  /** 보통 <ModalFooter/>. 없으면 푸터 영역을 그리지 않는다(읽기전용·행클릭형). */
  footer?: ReactNode;
  /** base=일반(z-99999), elevated=다른 모달 위 중첩(z-100001). 신청서 모달 위 모달 등. */
  layer?: "base" | "elevated";
  /** direct=기존 단독 Esc(기본), stack=부모 모달과 겹칠 때 최상단 하나만 닫기. */
  escMode?: "direct" | "stack";
  /** 헤더 닫기 X 노출. 기본 true. */
  showClose?: boolean;
  /** 본문 영역(스크롤되는 div)에 덧댈 클래스. 기본 패딩을 바꾸고 싶을 때. */
  bodyClassName?: string;
  /** panel(바깥 컨테이너)에 덧댈 클래스. */
  className?: string;
  /**
   * 모달 크기 커스텀(계정별 저장)을 켜는 안정적 식별 키. 주면 그 모달만 사용자가 모서리를 끌어
   * 크기를 바꾸고, 그 크기가 로그인 계정에 저장된다(+ 헤더에 "원래대로" 버튼). 안 주면(기본)
   * 리사이즈 기능은 통째로 꺼져 기존 동작과 100% 동일하다 — 옵트인. 키는 모달마다 고유해야 한다.
   */
  resizeKey?: string;
  /** 본문. min-h-0 flex-1 overflow-y-auto 가 자동 적용된다. */
  children: ReactNode;
}

export function ModalShell({
  isOpen,
  onClose,
  title,
  subtitle,
  size = "md",
  height = "auto",
  maxHeight = "85vh",
  headerExtra,
  footer,
  layer = "base",
  showClose = true,
  bodyClassName = "px-5 py-4",
  className = "",
  resizeKey,
  escMode = "direct",
  children,
}: ModalShellProps) {
  // 모달 크기 커스텀. resizeKey 없으면 enabled=false 로 모든 분기가 꺼진다(기존 동작 그대로).
  const { enabled, custom, save, reset } = useModalSize(resizeKey);
  const panelRef = useRef<HTMLDivElement>(null);
  // 드래그 중에만 채워지는 임시 크기. 드래그를 놓으면 save 후 null 로 비우고 custom 으로 수렴한다.
  const [dragSize, setDragSize] = useState<{ width: number; height: number } | null>(null);
  const dragRef = useRef<{ width: number; height: number } | null>(null);

  // 우하단 모서리 드래그 → panel 의 실제 px 에서 출발해 마우스 이동량만큼 키운다(마우스 전용).
  function startResize(e: React.PointerEvent) {
    if (!enabled || !panelRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = panelRef.current.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const onMove = (ev: PointerEvent) => {
      const next = {
        width: Math.max(280, rect.width + (ev.clientX - startX)),
        height: Math.max(200, rect.height + (ev.clientY - startY)),
      };
      dragRef.current = next;
      setDragSize(next);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      if (dragRef.current) save(dragRef.current);
      dragRef.current = null;
      setDragSize(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  // 중첩 모달은 전역 스택에 등록해 Esc 한 번에 최상단 하나만 닫는다.
  // 기존 ModalShell 호출처는 direct 기본값을 유지해 동작 범위를 넓히지 않는다.
  useModalEsc(isOpen && escMode === "stack", onClose);

  // 단독 모달의 기존 Esc 동작.
  useEffect(() => {
    if (!isOpen || escMode !== "direct") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose, escMode]);

  // 열려 있는 동안 배경 스크롤 잠금.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const zClass = layer === "elevated" ? "z-[100001]" : "z-99999";
  // 리셋 버튼은 헤더에 단다 — 커스텀 크기가 저장돼 있을 때만. 그래서 enabled 면 헤더를 항상 띄운다.
  const hasHeader =
    title != null || subtitle != null || headerExtra != null || showClose || enabled;

  // 저장된/드래그 중인 크기를 인라인 style 로 덮는다. 작은 화면을 넘지 않게 CSS min() 으로 클램프해
  // 큰 px 가 저장돼 있어도 모바일에서 화면 밖으로 안 튄다. eff 가 없으면(=커스텀 없음) 빈 객체라
  // 프리셋(SIZE_CLASS)·maxHeight 가 그대로 적용된다 → 기본 동작 무영향.
  const eff = enabled ? (dragSize ?? custom) : null;
  const resizeStyle: Record<string, string> = {};
  if (eff?.width) {
    // 상한은 "왼쪽 메뉴를 뺀" 폭이다 — 창은 메뉴 오른쪽부터 뜨므로(티켓 #160), 100vw 로 재면
    // 저장된 큰 폭이 오른쪽으로 넘친다. --lnb-w 는 메뉴가 없는 화면에선 0.
    resizeStyle.width = `min(${eff.width}px, calc(100vw - var(--lnb-w, 0px) - 2rem))`;
    resizeStyle.maxWidth = "none";
  }
  if (eff?.height) {
    const h = `min(${eff.height}px, 92vh)`;
    resizeStyle.height = h;
    resizeStyle.maxHeight = h;
  }

  return createPortal(
    // lnb-inset: 넓은 화면에서 왼쪽 메뉴를 비켜 그 오른쪽 끝부터 뜬다(티켓 #160, index.css).
    <div
      className={`fixed inset-0 lnb-inset flex items-center justify-center overflow-y-auto modal ${zClass}`}
    >
      {/* 딤 배경. 바깥 클릭으로는 닫지 않는다(입력 중 실수 닫힘 방지) — base Modal 과 동일.
          딤도 같이 비켜야 메뉴가 흐려지지 않고 그대로 보인다. */}
      <div className="fixed inset-0 lnb-inset bg-gray-400/50 backdrop-blur-[32px]" />
      <div
        ref={panelRef}
        className={`relative mx-4 my-8 flex w-full flex-col overflow-hidden rounded-3xl bg-white shadow-xl dark:bg-gray-900 ${SIZE_CLASS[size]} ${className}`}
        style={{ maxHeight, ...(height === "fixed" ? { height: maxHeight } : {}), ...resizeStyle }}
        onClick={(e) => e.stopPropagation()}
      >
        {hasHeader && (
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-100 px-5 py-3.5 dark:border-gray-800">
            <div className="min-w-0">
              {title != null && (
                <h3 className="truncate text-base font-semibold text-gray-800 dark:text-white/90">
                  {title}
                </h3>
              )}
              {subtitle != null && (
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {enabled && custom && (
                <button
                  type="button"
                  onClick={reset}
                  title="크기를 기본값으로 되돌립니다"
                  className="flex h-8 items-center gap-1 rounded-lg border border-gray-200 px-2.5 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:border-gray-700 dark:hover:bg-gray-800 dark:hover:text-white"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M3 12a9 9 0 1 0 3-6.7L3 8m0 0V3m0 5h5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  원래대로
                </button>
              )}
              {headerExtra}
              {showClose && (
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="닫기"
                  className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-white"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6.04 16.54a.9.9 0 1 0 1.27 1.27L12 13.14l4.69 4.67a.9.9 0 1 0 1.27-1.27L13.27 12l4.69-4.69a.9.9 0 1 0-1.27-1.27L12 10.73 7.31 6.04A.9.9 0 1 0 6.04 7.31L10.73 12l-4.69 4.54Z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}

        {/* 본문 — 내용만큼 늘다가 max-h 에서 여기만 스크롤. */}
        <div className={`min-h-0 flex-1 overflow-y-auto ${bodyClassName}`}>{children}</div>

        {footer != null && <div className="shrink-0">{footer}</div>}

        {/* 우하단 크기조절 그립 — 마우스 기기에서만(터치는 숨김). 끌어서 panel 폭·높이를 바꾼다. */}
        {enabled && (
          <div
            onPointerDown={startResize}
            title="드래그해서 크기를 조절하세요"
            className="absolute bottom-0 right-0 z-20 hidden h-5 w-5 cursor-nwse-resize touch-none items-end justify-end p-1 text-gray-400 hover:text-brand-500 dark:text-gray-500 [@media(pointer:fine)]:flex"
          >
            <svg width="11" height="11" viewBox="0 0 12 12" fill="currentColor" aria-hidden="true">
              <circle cx="9.5" cy="9.5" r="1" />
              <circle cx="9.5" cy="5.5" r="1" />
              <circle cx="5.5" cy="9.5" r="1" />
            </svg>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default ModalShell;
